import axios from 'axios';

// Get API URL from environment or use current origin
// If REACT_APP_API_URL is set, use it (but ensure it uses HTTPS in production)
// Otherwise, use the current origin (same protocol as the page)
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL+'api';
  
  if (envUrl) {
    // If environment URL is set, ensure it uses HTTPS in production
    if (window.location.protocol === 'https:' && envUrl.startsWith('http://')) {
      // Replace http:// with https:// for production
      return envUrl.replace('http://', 'https://');
    }
    return envUrl;
  }
  
  // Fallback: use current origin (same protocol as the page)
  // This ensures HTTPS pages use HTTPS API calls
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  
  // Default for server-side rendering
  return 'http://localhost:5000/api';
};

const API_URL = getApiUrl();

// Create axios instance
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't automatically redirect on 401 errors
    // Let individual components handle authentication errors
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  getDiscordAuthUrl: () => api.get('/auth/discord/url'),
  getMe: () => api.get('/auth/me'),
  updatePreferences: (preferences) => api.put('/auth/preferences', preferences),
  logout: () => api.post('/auth/logout'),
};

// Games API
export const gamesAPI = {
  getAvailableGames: (serverId) => api.get('/games/available', { 
    params: serverId ? { serverId } : {} 
  }),
  createGame: (gameData) => api.post('/games/create', gameData),
  getGame: (gameId) => api.get(`/games/${gameId}`),
  joinGame: (gameId) => api.post(`/games/${gameId}/join`),
  makeMove: (gameId, move) => api.post(`/games/${gameId}/move`, { move }),
  getGameHistory: (params) => api.get('/games/history/user', { params }),
  getActiveGames: () => api.get('/games/active/user'),
  abandonGame: (gameId) => api.post(`/games/${gameId}/abandon`),
  
  // New game management endpoints
  getGameRegistry: () => api.get('/admin/games/registry'),
  getGamesByTier: (tier) => api.get(`/admin/games/by-tier/${tier}`),
  getGamesByCategory: (category, tier) => api.get(`/admin/games/by-category/${category}`, {
    params: tier ? { tier } : {}
  }),
  getGameDetails: (gameId) => api.get(`/admin/games/${gameId}`),
  validateGameRegistry: () => api.post('/admin/games/validate'),
  getGameAccessMatrix: () => api.get('/admin/games/access-matrix'),
};

// Users API
export const usersAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (profileData) => api.put('/users/profile', profileData),
  getStats: () => api.get('/users/stats'),
  getLeaderboard: (params) => api.get('/users/leaderboard', { params }),
  getLimits: () => api.get('/users/limits'),
  deleteAccount: (confirmPassword) => api.delete('/users/account', { data: { confirmPassword } }),
};

// Leaderboard API
export const leaderboardAPI = {
  // Server leaderboard
  getServerLeaderboard: (serverId, params) => api.get(`/leaderboard/server/${serverId}`, { params }),
  
  // Global leaderboard
  getGlobalLeaderboard: (params) => api.get('/leaderboard/global', { params }),
  
  // Player stats
  getPlayerStats: (playerId, serverId, params) => api.get(`/leaderboard/player/${playerId}/server/${serverId}`, { params }),
  getPlayerGlobalStats: (playerId, params) => api.get(`/leaderboard/player/${playerId}/global`, { params }),
  
  // Server stats
  getServerStats: (serverId) => api.get(`/leaderboard/server/${serverId}/stats`),
  getServerRecentGames: (serverId, params) => api.get(`/leaderboard/server/${serverId}/recent`, { params }),
  
  // Update leaderboard (admin)
  updateServerLeaderboard: (serverId, data) => api.post(`/leaderboard/server/${serverId}/update`, data),
};

// Server Subscriptions API - Single source of truth for server subscriptions
export const serverSubscriptionsAPI = {
  // Get user's server subscriptions
  getUserServerSubscriptions: (userId) => api.get(`/server-subscriptions/user/${userId}`),
  
  // Get specific server subscription
  getServerSubscription: (serverId, serverName) => api.get(`/server-subscriptions/server/${serverId}`, { 
    params: serverName ? { serverName } : {} 
  }),
  
  // Subscribe to a plan
  createServerSubscription: (serverId, planId) => api.post(`/server-subscriptions/server/${serverId}/subscribe`, { planId }),
  
  // Manage subscription
  cancelServerSubscription: (serverId) => api.post(`/server-subscriptions/server/${serverId}/cancel`),
  reactivateServerSubscription: (serverId) => api.post(`/server-subscriptions/server/${serverId}/reactivate`),
  
  // Stripe portal for billing management
  createPortalSession: () => api.post('/server-subscriptions/portal'),
  
  // Get available subscription plans
  getServerPlans: () => api.get('/server-subscriptions/plans'),
  
  // Verify Stripe session and update subscription
  verifySession: (sessionId) => api.post(`/server-subscriptions/verify-session/${sessionId}`),
  
  // Simple verification that works without metadata
  verifySessionSimple: (sessionId, serverId) => api.post(`/server-subscriptions/verify-session-simple/${sessionId}`, { serverId }),
};

// Analytics API
export const analyticsAPI = {
  getBusinessAnalytics: () => api.get('/analytics/business'),
  getEngagementAnalytics: () => api.get('/analytics/engagement'),
  getGameAnalytics: (params) => api.get('/analytics/games', { params }),
};

// Discord API
export const discordAPI = {
  getBotInfo: () => api.get('/discord/bot-info'),
  getServers: () => api.get('/discord/servers'),
  getServer: (guildId) => api.get(`/discord/servers/${guildId}`),
  getInviteLink: () => api.get('/discord/invite'),
  getCommands: () => api.get('/discord/commands'),
  getServerSettings: (guildId) => api.get(`/discord/servers/${guildId}/settings`),
  updateServerSettings: (guildId, settings) => api.put(`/discord/servers/${guildId}/settings`, { settings }),
  getUserServers: () => api.get('/discord/servers'),
  getServerDetails: (guildId) => api.get(`/discord/servers/${guildId}`),
  getServerAnalytics: (guildId) => api.get(`/discord/servers/${guildId}/analytics`),
  getServerMembers: (guildId) => api.get(`/discord/servers/${guildId}/members`),
};

// Admin API - Use unified authentication (same as regular API)
const adminApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Use regular user token for admin requests (unified auth system)
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token'); // Use regular user token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const adminAPI = {
  getUsers: (params) => adminApi.get('/admin/users', { params }),
  getUser: (userId) => adminApi.get(`/admin/users/${userId}`),
  updateUserSubscription: (userId, data) => adminApi.put(`/admin/users/${userId}/subscription`, data),
  updateUserStatus: (userId, data) => adminApi.put(`/admin/users/${userId}/status`, data),
  getGames: (params) => adminApi.get('/admin/games', { params }),
  getGame: (gameId) => adminApi.get(`/admin/games/${gameId}`),
  deleteGame: (gameId) => adminApi.delete(`/admin/games/${gameId}`),
  getSystemStats: () => adminApi.get('/admin/stats'),
  getLogs: (params) => adminApi.get('/admin/logs', { params }),
  getSettings: () => adminApi.get('/admin/settings'),
  updateSettings: (settings) => adminApi.put('/admin/settings', settings),
  bulkOperation: (data) => adminApi.post('/admin/bulk/users', data),
  // SECURITY: Admin upgrade endpoint removed
  // Stripe settings
  getStripeSettings: () => adminApi.get('/stripe/settings'),
  updateStripeSettings: (settings) => adminApi.put('/stripe/settings', settings),
  testStripeConnection: () => adminApi.post('/stripe/settings/test'),
  // Stripe subscriptions
  getAllSubscriptions: (params) => adminApi.get('/stripe/admin/subscriptions', { params }),
  getSubscription: (subscriptionId) => adminApi.get(`/stripe/admin/subscriptions/${subscriptionId}`),
  updateSubscription: (subscriptionId, data) => adminApi.put(`/stripe/admin/subscriptions/${subscriptionId}`, data),
  deleteSubscription: (subscriptionId) => adminApi.delete(`/stripe/admin/subscriptions/${subscriptionId}`),
  // Server subscriptions
  getAllServerSubscriptions: (params) => adminApi.get('/server-subscriptions/admin/all', { params }),
  getServerSubscription: (subscriptionId) => adminApi.get(`/server-subscriptions/admin/${subscriptionId}`),
  updateServerSubscription: (subscriptionId, data) => adminApi.put(`/server-subscriptions/admin/${subscriptionId}`, data),
  cancelServerSubscription: (subscriptionId) => adminApi.post(`/server-subscriptions/admin/${subscriptionId}/cancel`),
  reactivateServerSubscription: (subscriptionId) => adminApi.post(`/server-subscriptions/admin/${subscriptionId}/reactivate`),
  getSubscriptionAnalytics: () => adminApi.get('/server-subscriptions/admin/analytics'),
  // Subscription Plans Management
  getSubscriptionPlans: () => adminApi.get('/subscription-plans'),
  updateSubscriptionPlan: (planId, planData) => adminApi.put(`/subscription-plans/${planId}`, planData),
  addSubscriptionPlan: (planData) => adminApi.post('/subscription-plans', planData),
  deleteSubscriptionPlan: (planId) => adminApi.delete(`/subscription-plans/${planId}`),
  seedSubscriptionPlans: () => adminApi.post('/subscription-plans/seed'),
};

// Stripe Settings API (admin only - consolidated)
export const stripeAPI = {
  getStripeSettings: () => api.get('/server-subscriptions/admin/stripe-settings'),
  updateStripeSettings: (settings) => api.put('/server-subscriptions/admin/stripe-settings', settings),
  testStripeConnection: () => api.post('/server-subscriptions/admin/stripe-settings/test'),
};

export default api;
